// misc
import { isEmpty } from 'lodash';

// components
import DkReadMore from '@v4/common/components/atoms/DkReadMore';
import VersionOptionals from '@v4/multi-section/components/organisms/VersionOptionals';
import DkOpenVideo from '@v4/common/components/molecules/DkOpenVideo';
import LoadingSpinner from '@v4/common/components/molecules/LoadingSpinner';
import { dkCurrency } from '@v4/common/filters';
import { EventBus } from '@v4/common/utils/event-bus';

// mixins
import { dkModalAware } from '@v4/common/components/mixins/modal';
import {
    modalCarConfigurator,
    modalWizardCarConfigurator,
    modalWizardLeadForm,
} from '@v4/common/components/mixins/modal-service';

// styles
import '@v4/multi-section/mixins/version/_index.scss';

export default ({ fetchedData }) => ({
    name: 'VersionInformationPage',
    /**
     * Custom directive using IntersectionObserver to toggle the bottom price bar.
     * The bar is shown only when the Main CTA element is no longer visible in the viewport after scrolling.
     **/
    directives: {
        intersect: {
            inserted(el, binding) {
                const options = { root: null, threshold: 0 };
                const observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        // Call the provided callback with the intersection entry
                        binding.value(entry);
                    });
                }, options);
                observer.observe(el);
                // Save the observer instance for later cleanup
                el._observer = observer;
            },
            unbind(el) {
                if (el._observer) {
                    el._observer.disconnect();
                }
            },
        },
    },
    data() {
        const version = fetchedData.version || {};

        return {
            selectedVersion: {
                price: version.rawPrice || '',
                basePrice: version.rawPrice || 0,
                versionId: version.versionId,
                versionHistoricalId: version.versionHistoricalId,
            },
            finalPrice: '',
            selectedOptionals: [],
            selectedColor: null,
            badges: fetchedData.versionsCountBox ? fetchedData.versionsCountBox.badges : [],
            shouldShowBottomConfigurationStepper: true,
            shouldShowBottomPriceBar: false,
        };
    },
    components: {
        VersionOptionals,
        DkOpenVideo,
        LoadingSpinner,
        ClickoutBox: () => ({
            component: import(/* webpackChunkName: "ClickoutBox-vue" */ '@v4/common/components/molecules/ClickoutBox'),
        }),
        DkButton: () => ({
            component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton'),
        }),
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon'),
        }),
        DkELabel: () => ({
            component: import(/* webpackChunkName: "DkELabel-vue" */ '@v4/common/components/atoms/DkELabel'),
        }),
        DkReadMore: () => ({
            component: import(/* webpackChunkName: "DkReadMore-vue" */ '@v4/common/components/atoms/DkReadMoreDST'),
        }),
        DkDlItem: () => ({
            component: import(/* webpackChunkName: "DkDlItem-vue" */ '@v4/common/components/atoms/DkDlItem'),
        }),
        DkAvailableVersionCard: () => ({
            component: import(
                /* webpackChunkName: "DkAvailableVersionCard-vue" */ '@v4/common/components/organisms/DkAvailableVersionCard.vue'
            ),
        }),
        DkConfigurationSummaryCard: () => ({
            component: import(
                /* webpackChunkName: "DkConfigurationSummaryCard-vue" */ '@v4/common/components/organisms/DkConfigurationSummaryCard.vue'
            ),
        }),
        DkStickyConfigurationStepper: () => ({
            component: import(
                /* webpackChunkName: "DkStickyConfigurationStepper-vue" */ '@v4/common/components/organisms/DkStickyConfigurationStepper.vue'
            ),
        }),
    },
    beforeMount() {
        this.$on('lead-form-mounted', this.handleLeadFormHiddenFields);
    },
    beforeDestroy() {
        this.$off('lead-form-mounted', this.handleLeadFormHiddenFields);
        EventBus.$off();
    },
    mounted() {
        DkReadMore().init();

        this.setSidebarMetaData({ title: this.fetchedData.title, subtitle: this.fetchedData.versionName });

        EventBus.$on('LeadForm:input', this.pushGABeginCheckout);
        EventBus.$on('WizardConfigureCarModal:close', this.handleCloseConfigurator);

        if (this.fetchedData && this.fetchedData.events && this.fetchedData.events['view_item']) {
            this.pushGtagEvent('view_item', null, this.fetchedData.events);
        }
    },
    mixins: [dkModalAware, modalCarConfigurator, modalWizardCarConfigurator, modalWizardLeadForm],
    computed: {
        leadFormMeta() {
            const title =
                this.fetchedData && this.fetchedData.makeName && this.fetchedData.submodelCommercialName
                    ? `${this.fetchedData.makeName} ${this.fetchedData.submodelCommercialName}`
                    : '';

            const subtitle = this.fetchedData && this.fetchedData.versionName ? `${this.fetchedData.versionName}` : '';

            return { title, subtitle };
        },
        optionalProps() {
            const price = this.selectedOptionals.length
                ? this.selectedOptionals.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0)
                : 0;

            return {
                count: this.selectedOptionals.length,
                price,
            };
        },
        showClickoutBox() {
            return this.fetchedData.clickout_section && !isEmpty(this.fetchedData.clickout_section);
        },
        listingPrice() {
            const { prices = [] } = this.fetchedData.version;
            const priceObj = prices.find(({ type }) => type && (type.slug === 'listing' || type.slug === 'offering'));

            if (!priceObj) {
                return '';
            }

            const numericPrice = parseInt(priceObj.originalPrice.replace(/[^\d]/g, ''), 10);
            return dkCurrency(numericPrice, this.$i18n.locale);
        },
        priceToShow() {
            return dkCurrency(this.finalPrice, this.$i18n.locale);
        },
    },
    methods: {
        dkCurrency,
        handleLeadFormHiddenFields(selectedVersion = null, leadFormWrapperRef) {
            const version = this.selectedVersion || selectedVersion || {};
            const leadFormWrapper = leadFormWrapperRef || this.$refs.leadFormWrapper || {};
            const finalPrice = this.finalPrice || version.price;
            const optionalsString = this.selectedOptionals.length
                ? this.selectedOptionals.map((item) => item.versionName).join()
                : null;

            leadFormWrapper.setLeadFormHiddenField('dkwp[price]', finalPrice.toString());
            leadFormWrapper.setLeadFormHiddenField('dkwp[vehicleId]', version.versionHistoricalId);
            leadFormWrapper.setLeadFormHiddenField('dkwp[optional]', optionalsString);
        },
        setOptionalsField(optionals) {
            this.selectedOptionals = optionals;
            EventBus.$emit('VersionPage:setOptionalsField', optionals);
        },
        setFinalPrice(finalPrice) {
            this.finalPrice = finalPrice;
        },
        trackWizardBtnClick() {
            this.pushGaEvent('VIP', 'CTA button click', 'VIP - CTA button click - Discover the price');
        },
        trackConfigureCarBtnClick() {
            this.pushGaEvent('VIP', 'CTA button click', 'VIP - CTA button click - Configure your car');
        },
        showWizardCarConfigurator() {
            this.trackWizardBtnClick();

            const {
                formData,
                formData: { project_id: projectId },
                formId,
                makeName,
                makeUrlCode,
                submodelCommercialName,
                submodelCommercialUrlCode,
                versionName,
                dealerSelection: hasDealerSelection,
            } = this.fetchedData;

            const props = {
                formData,
                formId,
                fullName: `${makeName} ${submodelCommercialName}`,
                make: makeUrlCode,
                model: submodelCommercialUrlCode,
                price: this.priceToShow,
                projectId: Number(projectId),
                versionName,
                hasDealerSelection,
                optionals: this.optionalProps,
            };

            this.clearClickToCallTimer();
            this.openWizardLeadFormModal(props);
        },
        configureCarBtnClick() {
            this.trackConfigureCarBtnClick();

            const el = document.getElementById('multi-section-version__configuration--js-scroller');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
            }
        },

        pushGABeginCheckout() {
            this.pushGtagEvent('begin_checkout', null, this.fetchedData.events);
        },

        handleCloseConfigurator() {
            this.pushGtagEvent('click_close_configurator', null, this.fetchedData.events);
        },
        handleIntersection(entry) {
            // Set the flag to true when the main-cta element is not intersecting (i.e. not visible)
            this.shouldShowBottomPriceBar = !entry.isIntersecting;
        },
    },
});
